import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { CheckCircle, Circle, Clock, User, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const TaskPreview = ({ taskId, onNavigate }) => {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTaskPreview();
  }, [taskId]);

  const fetchTaskPreview = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/tasks/${taskId}/preview`,
        { withCredentials: true }
      );
      setTask(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching task preview:', err);
      setError('Task not found');
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return <Circle className="h-4 w-4" />;
    
    switch(status.value) {
      case 'done':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in-progress':
        return <Circle className="h-4 w-4 text-blue-600 fill-blue-600" />;
      default:
        return <Circle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getPriorityColor = (priority) => {
    if (!priority) return 'bg-gray-100 text-gray-700';
    
    switch(priority.value) {
      case 'urgent':
        return 'bg-red-100 text-red-700';
      case 'high':
        return 'bg-orange-100 text-orange-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-500">
        <Clock className="h-4 w-4 animate-spin" />
        Loading task...
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200 text-sm text-red-600">
        <AlertCircle className="h-4 w-4" />
        {error || 'Task not found'}
      </div>
    );
  }

  const handleClick = () => {
    if (onNavigate) {
      onNavigate(task.workspace_id, task.boardId, taskId);
    }
  };

  return (
    <div 
      onClick={handleClick}
      className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer max-w-lg group"
    >
      {/* Status Icon */}
      <div className="flex-shrink-0">
        {getStatusIcon(task.status)}
      </div>

      {/* Task Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-gray-900 truncate">
            {task.task}
          </span>
          <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {/* Status Badge */}
          <span className={`px-2 py-0.5 rounded-full ${
            task.status?.value === 'done' ? 'bg-green-100 text-green-700' :
            task.status?.value === 'in-progress' ? 'bg-blue-100 text-blue-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {task.status?.label || 'To Do'}
          </span>

          {/* Priority Badge */}
          {task.priority && (
            <span className={`px-2 py-0.5 rounded-full ${getPriorityColor(task.priority)}`}>
              {task.priority.label}
            </span>
          )}

          {/* Assignees */}
          {task.assignees && task.assignees.length > 0 && (
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>{task.assignees[0].name}</span>
              {task.assignees.length > 1 && (
                <span>+{task.assignees.length - 1}</span>
              )}
            </div>
          )}

          {/* Due Date */}
          {task.date && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{new Date(task.date).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskPreview;
